import {
    useEffect,
    Fragment,
    useState,
    useRef
} from "react";
import {
    Platform,
    View
} from "react-native";
import type ITimeSelectorProps from "./type";
import {
    type TimeSelectorGroupEvaluation,
    type TimeSelectorDayPeriods,
    type TimeSelectorRangeError,
    type TimeSelectorDayPeriod,
    type TimeSelectorFieldKey,
    type TimeSelectorDrafts,
    type TimeSelectorHelper,
    type TimeSelectorField
} from "./type";
import stylesheet, {
    TIME_SELECTOR_DAY_PERIOD_LABEL_KEY,
    TIME_SELECTOR_TWELVE_HOUR_MINIMUM,
    TIME_SELECTOR_FIELD_LABEL_KEYS,
    TIME_SELECTOR_GROUP_LABEL_KEYS,
    TIME_SELECTOR_RANGE_ERROR_KEYS,
    TIME_SELECTOR_PLACEHOLDER_KEYS,
    TIME_SELECTOR_DAY_PERIOD_KEYS,
    TIME_SELECTOR_UNIT_RANGE_KEYS,
    getTimeSelectorDayPeriodShift,
    getTimeSelectorDurationText,
    getTimeSelectorUnitMaximum,
    TIME_SELECTOR_DAY_PERIODS,
    getTimeSelectorLabelColor,
    getTimeSelectorEvaluation,
    getTimeSelectorNextDrafts,
    TIME_SELECTOR_LIMIT_KEYS,
    getTimeSelectorUnitValue,
    getTimeSelectorLimitText,
    isSameTimeSelectorDrafts,
    getTimeSelectorValueKey,
    getTimeSelectorUnitText,
    getTimeSelectorCommit,
    useStyles
} from "./stylesheet";
import TimeSelectorHelperPresence from "./components/helperPresence";
import TimeInput from "./components/timeInput";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    CircleAlert as CircleAlertIcon
} from "lucide-react-native";
import {
    uuid
} from "../../utils";
import Tabs from "../tabs";
import Text from "../text";

const TimeSelector = ({
    isTimeSelectWorksWithCenterWhenTimePicker,
    isWorkWith24HoursFormat = true,
    multipleSelectMinimumRequireMS,
    isWorkWithSeconds = false,
    multipleSelectMaximumMS,
    setIsSheetContentReady,
    labelPosition = "top",
    selectMultipleObject,
    isDisabled = false,
    isReadOnly = false,
    customLocalize,
    selectObject,
    customTheme,
    pickerType,
    dateRange,
    maxDate,
    minDate,
    variant,
    date
}: ITimeSelectorProps) => {
    const {
        typography,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const generatedID = useRef(uuid());

    const [
        focusedField,
        setFocusedField
    ] = useState<TimeSelectorFieldKey | undefined>(undefined);

    const [
        drafts,
        setDrafts
    ] = useState<TimeSelectorDrafts>({});

    const [
        dayPeriods,
        setDayPeriods
    ] = useState<TimeSelectorDayPeriods>({});

    const isCentered = pickerType === "time" && !!isTimeSelectWorksWithCenterWhenTimePicker;

    const webDirectionProps = Platform.OS === "web" ? {
        dir: "ltr"
    } : {};

    const valueKey = getTimeSelectorValueKey({
        dateRange,
        date
    });

    const labelColor = getTimeSelectorLabelColor({
        isDisabled,
        colors
    });

    const {
        fieldsContainer: fieldsContainerDynamicStyle,
        groupsContainer: groupsContainerDynamicStyle,
        helperIcon: helperIconDynamicStyle,
        container: containerDynamicStyle,
        dayPeriod: dayPeriodDynamicStyle,
        helper: helperDynamicStyle,
        group: groupDynamicStyle
    } = useStyles({
        labelPosition,
        isCentered,
        typography,
        variant,
        spaces
    });

    const getEvaluation = ({
        focusedField: evaluationFocusedField,
        drafts: evaluationDrafts
    }: {
        focusedField?: TimeSelectorFieldKey;
        drafts: TimeSelectorDrafts;
    }) => {
        return getTimeSelectorEvaluation({
            focusedField: evaluationFocusedField,
            multipleSelectMinimumRequireMS,
            drafts: evaluationDrafts,
            isWorkWith24HoursFormat,
            multipleSelectMaximumMS,
            isWorkWithSeconds,
            dayPeriods,
            dateRange,
            maxDate,
            minDate,
            variant,
            date
        });
    };

    const evaluation = getEvaluation({
        focusedField,
        drafts
    });

    useEffect(() => {
        if(pickerType === "time" && setIsSheetContentReady) {
            setIsSheetContentReady(true);
        }
    }, []);

    useEffect(() => {
        setDrafts((previousDrafts) => {
            const nextDrafts = getTimeSelectorNextDrafts({
                evaluation: getEvaluation({
                    drafts: previousDrafts,
                    focusedField
                }),
                isWorkWith24HoursFormat,
                drafts: previousDrafts,
                focusedField
            });

            return isSameTimeSelectorDrafts({
                first: previousDrafts,
                second: nextDrafts
            }) ? previousDrafts : nextDrafts;
        });
    }, [
        valueKey
    ]);

    const getGroupErrorMessage = (groupEvaluation: TimeSelectorGroupEvaluation) => {
        const error = groupEvaluation.error;

        if(!error) {
            return undefined;
        }

        if(error.type === "unitRange") {
            return localize(TIME_SELECTOR_UNIT_RANGE_KEYS[error.unit], [
                getTimeSelectorUnitText({
                    value: error.unit === "hours" && !isWorkWith24HoursFormat ? TIME_SELECTOR_TWELVE_HOUR_MINIMUM : 0
                }),
                getTimeSelectorUnitText({
                    value: getTimeSelectorUnitMaximum({
                        isWorkWith24HoursFormat,
                        unit: error.unit
                    })
                })
            ]);
        }

        const limit = error.type === "minDate" ? minDate : maxDate;

        if(!limit) {
            return undefined;
        }

        return localize(TIME_SELECTOR_LIMIT_KEYS[error.type], [
            getTimeSelectorLimitText({
                date: groupEvaluation.candidate,
                isWorkWith24HoursFormat,
                locale: activeLocale,
                isWorkWithSeconds,
                limit
            })
        ]);
    };

    const getRangeErrorMessage = (rangeError: TimeSelectorRangeError) => {
        if(rangeError.type === "minimumGap" && multipleSelectMinimumRequireMS) {
            return localize(TIME_SELECTOR_RANGE_ERROR_KEYS.minimumGap, [
                getTimeSelectorDurationText({
                    value: multipleSelectMinimumRequireMS,
                    locale: activeLocale
                })
            ]);
        }

        if(rangeError.type === "maximumGap" && multipleSelectMaximumMS) {
            return localize(TIME_SELECTOR_RANGE_ERROR_KEYS.maximumGap, [
                getTimeSelectorDurationText({
                    value: multipleSelectMaximumMS,
                    locale: activeLocale
                })
            ]);
        }

        return localize(TIME_SELECTOR_RANGE_ERROR_KEYS[rangeError.type]);
    };

    const getHelperID = (name: string) => `${generatedID.current}-${name}-helper`;

    const getHelpers = (): Array<TimeSelectorHelper> => {
        if(isDisabled) {
            return [];
        }

        const helpers: Array<TimeSelectorHelper> = [];

        evaluation.groups.forEach((groupEvaluation) => {
            const message = getGroupErrorMessage(groupEvaluation);

            if(!message) {
                return;
            }

            helpers.push({
                id: getHelperID(groupEvaluation.group),
                groups: [
                    groupEvaluation.group
                ],
                message
            });
        });

        if(evaluation.rangeError) {
            helpers.push({
                message: getRangeErrorMessage(evaluation.rangeError),
                groups: evaluation.rangeError.invalidGroups,
                id: getHelperID("range")
            });
        }

        return helpers;
    };

    const helpers = getHelpers();

    const helperIDs = [
        ...evaluation.groups.map((groupEvaluation) => getHelperID(groupEvaluation.group)),
        getHelperID("range")
    ];

    const onFieldChangeText = ({
        groupEvaluation,
        field,
        text
    }: {
        groupEvaluation: TimeSelectorGroupEvaluation;
        field: TimeSelectorField;
        text: string;
    }) => {
        const nextDrafts: TimeSelectorDrafts = {
            ...drafts,
            [field.key]: {
                baseValue: getTimeSelectorUnitValue({
                    date: groupEvaluation.date,
                    isWorkWith24HoursFormat,
                    unit: field.unit
                }),
                text
            }
        };

        const nextEvaluation = getEvaluation({
            focusedField: field.key,
            drafts: nextDrafts
        });

        const commit = getTimeSelectorCommit({
            evaluation: nextEvaluation,
            variant
        });

        setDrafts(getTimeSelectorNextDrafts({
            evaluation: nextEvaluation,
            focusedField: field.key,
            isWorkWith24HoursFormat,
            drafts: nextDrafts,
            commit
        }));

        if(nextEvaluation.rangeError && nextEvaluation.rangeError.type !== evaluation.rangeError?.type) {
            NCoreUIKitToast.open({
                title: getRangeErrorMessage(nextEvaluation.rangeError)
            });
        }

        if(commit?.date) {
            selectObject(commit.date);
        }

        if(commit?.dateRange) {
            selectMultipleObject(commit.dateRange);
        }
    };

    const onFieldFocus = (field: TimeSelectorField) => {
        setFocusedField(field.key);
    };

    const onFieldBlur = (field: TimeSelectorField) => {
        setFocusedField((previousField) => previousField === field.key ? undefined : previousField);

        setDrafts((previousDrafts) => getTimeSelectorNextDrafts({
            evaluation: getEvaluation({
                drafts: previousDrafts
            }),
            isWorkWith24HoursFormat,
            drafts: previousDrafts
        }));
    };

    const onDayPeriodChange = (groupEvaluation: TimeSelectorGroupEvaluation, dayPeriod: TimeSelectorDayPeriod) => {
        setDayPeriods((previousDayPeriods) => ({
            ...previousDayPeriods,
            [groupEvaluation.group]: dayPeriod
        }));

        const shift = getTimeSelectorDayPeriodShift({
            multipleSelectMinimumRequireMS,
            multipleSelectMaximumMS,
            isWorkWithSeconds,
            groupEvaluation,
            evaluation,
            dayPeriod,
            maxDate,
            variant,
            minDate
        });

        const message = shift.error ? getGroupErrorMessage({
            ...groupEvaluation,
            candidate: shift.candidate,
            error: shift.error
        }) : shift.rangeError && getRangeErrorMessage(shift.rangeError);

        if(message) {
            NCoreUIKitToast.open({
                title: message
            });
        }

        if(shift.commit?.date) {
            selectObject(shift.commit.date);
        }

        if(shift.commit?.dateRange) {
            selectMultipleObject(shift.commit.dateRange);
        }
    };

    const renderSeparator = (index: number) => {
        if(!index) {
            return null;
        }

        return <Text
            variant="titleMediumSize"
            customTheme={customTheme}
            aria-hidden={true}
            color="low"
        >
            :
        </Text>;
    };

    const renderField = (groupEvaluation: TimeSelectorGroupEvaluation, field: TimeSelectorField, index: number) => {
        const fieldHelpers = helpers.filter((helper) => helper.groups.includes(groupEvaluation.group));

        return <Fragment
            key={field.key}
        >
            {renderSeparator(index)}
            <TimeInput
                aria-label={localize(TIME_SELECTOR_FIELD_LABEL_KEYS[groupEvaluation.group][field.unit])}
                helperText={fieldHelpers.map((helper) => helper.message).join(" ")}
                placeholder={localize(TIME_SELECTOR_PLACEHOLDER_KEYS[field.unit])}
                helperID={fieldHelpers.map((helper) => helper.id).join(" ")}
                onChangeText={(text) => onFieldChangeText({
                    groupEvaluation,
                    field,
                    text
                })}
                isInvalid={!isDisabled && field.isInvalid}
                id={`${generatedID.current}-${field.key}`}
                onFocus={() => onFieldFocus(field)}
                onBlur={() => onFieldBlur(field)}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                value={field.text}
            />
        </Fragment>;
    };

    const renderDayPeriodTrigger = (dayPeriod: TimeSelectorDayPeriod) => {
        return <Tabs.Trigger
            title={localize(TIME_SELECTOR_DAY_PERIOD_KEYS[dayPeriod])}
            customLocalize={customLocalize}
            customTheme={customTheme}
            value={dayPeriod}
            key={dayPeriod}
        />;
    };

    const renderDayPeriod = (groupEvaluation: TimeSelectorGroupEvaluation) => {
        if(isWorkWith24HoursFormat) {
            return null;
        }

        return <Tabs
            onValueChange={(dayPeriod) => onDayPeriodChange(groupEvaluation, dayPeriod === "pm" ? "pm" : "am")}
            isDisabled={isDisabled || isReadOnly}
            value={groupEvaluation.dayPeriod}
            customLocalize={customLocalize}
            style={dayPeriodDynamicStyle}
            customTheme={customTheme}
            listRole="radiogroup"
            variant="segment"
            size="medium"
        >
            <Tabs.List
                title={localize(TIME_SELECTOR_DAY_PERIOD_LABEL_KEY)}
                customTheme={customTheme}
            >
                {TIME_SELECTOR_DAY_PERIODS.map((dayPeriod) => renderDayPeriodTrigger(dayPeriod))}
            </Tabs.List>
        </Tabs>;
    };

    const renderGroup = (groupEvaluation: TimeSelectorGroupEvaluation) => {
        const labelID = `${generatedID.current}-${groupEvaluation.group}-label`;

        return <View
            key={groupEvaluation.group}
            aria-labelledby={labelID}
            style={[
                stylesheet.group,
                groupDynamicStyle
            ]}
            role="group"
        >
            <Text
                variant="labelMediumSize"
                customTheme={customTheme}
                customColor={labelColor}
                style={stylesheet.label}
                id={labelID}
            >
                {localize(TIME_SELECTOR_GROUP_LABEL_KEYS[groupEvaluation.group])}
            </Text>
            <View
                {...webDirectionProps}
                style={[
                    stylesheet.fieldsContainer,
                    fieldsContainerDynamicStyle
                ]}
            >
                {groupEvaluation.fields.map((field, index) => renderField(groupEvaluation, field, index))}
                {renderDayPeriod(groupEvaluation)}
            </View>
        </View>;
    };

    const renderHelper = (helper: TimeSelectorHelper) => {
        return <View
            style={[
                stylesheet.helper,
                helperDynamicStyle
            ]}
            key={helper.id}
            id={helper.id}
        >
            <View
                style={[
                    stylesheet.helperIcon,
                    helperIconDynamicStyle
                ]}
                aria-hidden={true}
            >
                <CircleAlertIcon
                    color={colors.content.text.danger}
                    size={iconSizes.small}
                />
            </View>
            <Text
                customColor={colors.content.text.danger}
                style={stylesheet.helperText}
                customTheme={customTheme}
                variant="bodyMediumSize"
            >
                {helper.message}
            </Text>
        </View>;
    };

    const renderHelperSlot = (helperID: string) => {
        const helper = helpers.find((helperItem) => helperItem.id === helperID);

        return <TimeSelectorHelperPresence
            offset={-spaces.spacingXs}
            customTheme={customTheme}
            isVisible={!!helper}
            key={helperID}
        >
            {helper ? renderHelper(helper) : null}
        </TimeSelectorHelperPresence>;
    };

    return <View
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.groupsContainer,
                groupsContainerDynamicStyle
            ]}
        >
            {evaluation.groups.map((groupEvaluation) => renderGroup(groupEvaluation))}
        </View>
        {helperIDs.map((helperID) => renderHelperSlot(helperID))}
    </View>;
};
export default TimeSelector;
